import { Slot } from '@radix-ui/react-slot'
import {
  cva,
  type VariantProps,
} from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-all cursor-pointer [&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none text-white ' +
  'disabled:cursor-not-allowed disabled:text-foreground-disabled',
  {
    variants: {
      variant: {
        default:
          'bg-white/8 hover:bg-white/16 active:bg-white/20 text-foreground',
        destructive:
          'bg-destructive hover:bg-destructive-hover active:bg-destructive-active disabled:bg-destructive-disabled',
        primary:
          'bg-primary hover:bg-primary-hover active:bg-primary-active disabled:bg-primary-disabled',
        secondary:
          'bg-secondary hover:bg-secondary-hover active:bg-secondary-active disabled:bg-secondary-disabled',
        outline:
          'border border-white/20 [&:hover,&:active]:border-primary [&:hover,&:active]:text-primary disabled:border-white/8',
        success:
          'bg-success hover:bg-success-hover active:bg-success-active disabled:bg-success-disabled',
        warning:
          'bg-warning hover:bg-warning-hover active:bg-warning-active disabled:bg-warning-disabled',
        'text-primary':
          'text-primary hover:text-primary-hover active:text-primary-active disabled:text-primary-disabled',
        'text-secondary':
          'text-secondary hover:text-secondary-hover active:text-secondary-active disabled:text-secondary-disabled',
        'text-default':
          'text-foreground hover:text-foreground-secondary active:text-foreground-tertiary',
        'text-destructive':
          'text-destructive hover:text-destructive-hover active:text-destructive-active disabled:text-destructive-disabled',
        'text-success':
          'text-success hover:text-success-hover active:text-success-active disabled:text-success-disabled',
        'text-warning':
          'text-warning hover:text-warning-hover active:text-warning-active disabled:text-warning-disabled',
        icon:
          'hover:bg-white/8 rounded-md',
      },
      size: {
        xl: 'h-12 px-6 min-w-24 text-base',
        lg: 'h-10 px-5 min-w-20 text-sm',
        default: 'h-9 px-4 min-w-18 text-sm',
        sm: 'h-7 gap-1.5 px-2 min-w-15 text-xs',
      },
    },
    compoundVariants: [{
      size: 'default',
      variant: 'icon',
      class: 'size-6 min-w-0 px-0',
    }, {
      size: 'lg',
      variant: 'icon',
      class: 'size-10 min-w-0 px-0',
    }, {
      size: 'sm',
      variant: 'icon',
      class: 'size-5 min-w-0 px-0 rounded-sm',
    }],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  children,
  asChild = false,
  isLoading = false,
  disabled,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
  asChild?: boolean
  isLoading?: boolean
}) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading && <Loader2 className="animate-spin size-4" />}
      {children}
    </Comp>
  )
}

export {
  Button,
  buttonVariants,
}
